import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BEVERAGE_TYPES } from '../lib/constants';
import { AddCocktailForm } from './AddCocktailForm';
import { AddDrinkForm } from './AddDrinkForm';
import './CategoryComponent.scss';
import { CategoryHeader } from './CategoryHeader';
import CocktailComponent from './CocktailComponent';
import DrinkComponent from './DrinkComponent';

export default function CategoryComponent({
  category,
  onDelete,
  onUpdate,
  onBeverageAdd,
  onBeverageUpdate,
  onBeverageDelete,
  onBeverageMove,
  beverages,
  dragHandleProps,

  canBeDragged,
}) {
  const { type } = category;

  const { t } = useTranslation();

  const handleAddBeverage = beverage => onBeverageAdd(beverage, category);

  const handleUpdateBeverage = beverage => onBeverageUpdate(beverage, category);

  const handleBeverageDelete = beverage => onBeverageDelete(beverage, category);

  // category being "required" means it contains required items
  const required = useMemo(() => Boolean(beverages.filter(({required}) => required).length), [beverages]);

  // allow deletion only on empty categories
  const deleteable = !beverages.length;

  const editable = !required;

  const resolveDropIndex = event => {
    const listItems = Array.from(
      event.currentTarget.querySelectorAll('[data-beverage-index]')
    );

    if (!listItems.length) return 0;

    const pointerY = event.clientY;

    for (const item of listItems) {
      const rect = item.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;

      if (pointerY < midpoint) {
        return Number(item.dataset.beverageIndex);
      }
    }

    return listItems.length;
  };

  const handleBeverageDragOver = event => {
    const payloadRaw = event.dataTransfer.getData('application/x-brown-forman-dnd');

    if (!payloadRaw) return;

    try {
      const payload = JSON.parse(payloadRaw);
      if (payload.dragType !== 'beverage') return;

      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    } catch (e) {
      // Ignore malformed drag payloads from other sources.
    }
  };

  const handleBeverageDrop = async event => {
    const payloadRaw = event.dataTransfer.getData('application/x-brown-forman-dnd');

    if (!payloadRaw) return;

    try {
      const payload = JSON.parse(payloadRaw);
      if (payload.dragType !== 'beverage') return;

      event.preventDefault();

      const sourceCategoryId = String(payload.sourceCategoryId);
      const destinationCategoryId = String(category.id);
      const sourceIndex = Number(payload.sourceIndex);
      const destinationIndexRaw = resolveDropIndex(event);
      const destinationIndex =
        sourceCategoryId === destinationCategoryId &&
        sourceIndex < destinationIndexRaw
          ? destinationIndexRaw - 1
          : destinationIndexRaw;

      if (
        sourceCategoryId === destinationCategoryId &&
        sourceIndex === destinationIndex
      ) {
        return;
      }

      await onBeverageMove(
        sourceIndex,
        destinationIndex,
        sourceCategoryId,
        destinationCategoryId,
        String(payload.beverageId)
      );
    } catch (e) {
      console.error(e);
      window.alert(t('itemUpdateError_plural'));
    }
  };

  return (
    <div className="col-12">
      <div className="card mb-4 category-component">
        <CategoryHeader
          editable={editable}
          deleteable={deleteable}
          category={category}
          onCategoryDelete={onDelete}
          onCategoryUpdate={onUpdate}
          dragHandleProps={dragHandleProps}

          canBeDragged={canBeDragged}
        />

        {beverages.length ? (
          <div
            className="list-group list-group-flush"
            onDragOver={handleBeverageDragOver}
            onDrop={handleBeverageDrop}
          >
            {beverages.map((beverage, index) => (
              <div
                className="list-group-item"
                data-beverage-index={index}
                key={beverage.id}
              >
                {type === BEVERAGE_TYPES.COCKTAIL ? (
                  <CocktailComponent
                    key={beverage.id}
                    item={beverage}
                    onUpdate={handleUpdateBeverage}
                    onDelete={handleBeverageDelete}
                    dragHandleProps={
                      beverage.required
                        ? {}
                        : {
                            draggable: true,
                            onDragStart: event => {
                              event.stopPropagation();
                              event.dataTransfer.effectAllowed = 'move';
                              const payload = JSON.stringify({
                                dragType: 'beverage',
                                beverageId: String(beverage.id),
                                sourceCategoryId: String(category.id),
                                sourceIndex: index,
                              });
                              event.dataTransfer.setData(
                                'application/x-brown-forman-dnd',
                                payload
                              );
                              event.dataTransfer.setData('text/plain', payload);
                            },
                          }
                    }
                  />
                ) : (
                  <DrinkComponent
                    key={beverage.id}
                    item={beverage}
                    onUpdate={handleUpdateBeverage}
                    onDelete={handleBeverageDelete}
                    dragHandleProps={
                      beverage.required
                        ? {}
                        : {
                            draggable: true,
                            onDragStart: event => {
                              event.stopPropagation();
                              event.dataTransfer.effectAllowed = 'move';
                              const payload = JSON.stringify({
                                dragType: 'beverage',
                                beverageId: String(beverage.id),
                                sourceCategoryId: String(category.id),
                                sourceIndex: index,
                              });
                              event.dataTransfer.setData(
                                'application/x-brown-forman-dnd',
                                payload
                              );
                              event.dataTransfer.setData('text/plain', payload);
                            },
                          }
                    }
                    category={category}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div
            className="card-body border-start border-end"
            onDragOver={handleBeverageDragOver}
            onDrop={handleBeverageDrop}
          >
            <p className="btn d-flex justify-content-center mb-0">
              {t('emptyCategory')}
            </p>
          </div>
        )}

        <div className="card-footer bg-gray-200 border-0">
          {type === BEVERAGE_TYPES.COCKTAIL && (
            <AddCocktailForm onSubmit={handleAddBeverage} category={category} />
          )}

          {type === BEVERAGE_TYPES.DRINK && (
            <AddDrinkForm onSubmit={handleAddBeverage} category={category} />
          )}
        </div>
      </div>
    </div>
  );
}
